import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { App, Card, Drawer, Form, Input, Spin, Switch, Tooltip, Typography } from 'antd';

import CalculateCommissionAffiliate from 'components/CalculateCommissionAffiliate';
import ColoredFormattedNumber from 'components/ColoredFormattedNumber';
import CustomCopyToClipboard from 'components/CustomCopyToClipboard';
import ModalActions from 'components/ModalActions';
import { functions } from 'helpers';
import { HOST_ORBITA } from 'settings';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import ActionCollapse from 'v2/components/Shared/QuickView/ActionCollapse';
import { useProductInviteAffiliate } from 'v2/hooks/useProductInviteAffiliate';
import { useProductStatusMutation } from 'v2/hooks/useProductStatusMutation';
import productService from 'v2/services/product';

import { Icon } from '@eduzz/ui-antd-theme';

import QuickViewLinks from './QuickviewLinks';

interface IProps {
  active: boolean;
  changeActive: any;
  onEditProduct?: any;
  productData: any;
}

const QuickView: React.FC<IProps> = ({ active, changeActive, onEditProduct, productData }) => {
  const history = useHistory();
  const { message } = App.useApp();
  const statusMutation = useProductStatusMutation();
  const inviteAffiliateMutation = useProductInviteAffiliate();

  const [idProductHistory, setIdProductHistory] = useState<number>(null);
  const [modalStatusProduct, setModalStatusProduct] = useState(false);
  const [modalStatusProductArchive, setModalStatusProductArchive] = useState(false);
  const [modalCloneProduct, setModalCloneProduct] = useState(false);
  const [statusProduct, setStatusProduct] = useState<'active' | 'inactive' | 'filed' | ' '>(null);
  const [loading, setLoading] = useState(true);
  const [, setLoadingCloneProduct] = useState(false);
  const [inputInviteAffiliate, setInputInviteAffiliate] = useState<string>(null);
  const [, setInputInviteAffiliateError] = useState(false);
  const [inputDelivery, setInputDelivery] = useState<string>(null);
  const [, setInputDeliveryError] = useState(false);
  const [, setProductInvoiceData] = useState<any>(null);
  const [, setActionButtonDisabled] = useState(false);
  const [numberAffiliates, setNumberAffiliates] = useState(0);
  const [validationErrors] = useState<Record<string, string>>({});

  const productDataRef = useRef(productData);
  productDataRef.current = productData;

  const statusProductRef = useRef(statusProduct);
  statusProductRef.current = statusProduct;

  const prevActiveRef = useRef(active);

  const isValid = useCallback(
    (field: any) => {
      return !!validationErrors[field];
    },
    [validationErrors]
  );

  const errorFor = useCallback(
    (field: any) => {
      return validationErrors[field] || '';
    },
    [validationErrors]
  );

  const handleCloseQuickView = useCallback(() => {
    if (typeof changeActive === 'function') {
      changeActive();
    }

    setLoading(true);
    setIdProductHistory(productDataRef.current?.id);
  }, [changeActive]);

  const toggleModalStatusProduct = useCallback(() => {
    setModalStatusProduct(prev => !prev);
  }, []);

  const onCancelModal = useCallback(() => {
    toggleModalStatusProduct();
  }, [toggleModalStatusProduct]);

  const onConfirmModal = useCallback(async () => {
    toggleModalStatusProduct();
    setActionButtonDisabled(true);

    try {
      await statusMutation.mutateAsync({
        productId: productDataRef.current.id,
        status: statusProductRef.current === 'active' ? 0 : 1
      });

      setStatusProduct(statusProductRef.current === 'active' ? 'inactive' : 'active');
      setActionButtonDisabled(false);

      if (onEditProduct) {
        onEditProduct();
      }
    } catch {
      setActionButtonDisabled(false);
      message.error('Ops, erro ao atualizar o status do produto');
    }
  }, [statusMutation, onEditProduct, toggleModalStatusProduct, message]);

  const toggleModalStatusProductArchive = useCallback(() => {
    setModalStatusProductArchive(prev => !prev);
  }, []);

  const onCancelModalArchive = useCallback(() => {
    toggleModalStatusProductArchive();
  }, [toggleModalStatusProductArchive]);

  const onConfirmModalArchive = useCallback(async () => {
    setActionButtonDisabled(true);
    toggleModalStatusProductArchive();

    try {
      await statusMutation.mutateAsync({
        productId: productDataRef.current.id,
        status: 2
      });

      setStatusProduct('filed');
      setActionButtonDisabled(false);

      if (onEditProduct) {
        onEditProduct();
      }
    } catch {
      setActionButtonDisabled(false);
      message.error('Ops, não foi possível excluir o produto');
    }
  }, [statusMutation, onEditProduct, toggleModalStatusProductArchive, message]);

  const toggleModalCloneProduct = useCallback(() => {
    setModalCloneProduct(prev => !prev);
  }, []);

  const onCancelModalCloneProduct = useCallback(() => {
    toggleModalCloneProduct();
  }, [toggleModalCloneProduct]);

  const onConfirmModalCloneProduct = useCallback(async () => {
    toggleModalCloneProduct();
    setLoadingCloneProduct(true);
    setActionButtonDisabled(true);

    try {
      const result = await productService.cloneProduct(productDataRef.current.id);
      const clonedProduct = result?.product ?? result;
      history.push('/producer/product/editproduct/' + clonedProduct.id);
      functions.addScrollPage();
    } catch {
      message.error('Ops, erro ao clonar o produto');
      setLoadingCloneProduct(false);
      setActionButtonDisabled(false);
    }
  }, [history, toggleModalCloneProduct, message]);

  const handleChange = useCallback(
    (prop: any) => (event: any) => {
      if (prop === 'inputInviteAffiliate') {
        setInputInviteAffiliateError(false);
        setInputInviteAffiliate(event.target.value);
      }

      if (prop === 'inputDelivery') {
        setInputDelivery(event.target.value);
      }
    },
    []
  );

  const handleClickInviteAffiliate = useCallback(async () => {
    setInputInviteAffiliateError(false);

    try {
      await inviteAffiliateMutation.mutateAsync({
        productId: productDataRef.current.id,
        email: inputInviteAffiliate
      });

      setInputInviteAffiliate('');
      setInputInviteAffiliateError(false);
      setNumberAffiliates(prev => prev + 1);

      if (onEditProduct) {
        onEditProduct();
      }

      message.success('Sucesso, afiliado adicionado com êxito');
    } catch {
      message.error('Ops, erro ao convidar afiliado');
    }
  }, [inviteAffiliateMutation, inputInviteAffiliate, onEditProduct, message]);

  const handleChangeStatusProduct = useCallback(() => {
    toggleModalStatusProduct();
  }, [toggleModalStatusProduct]);

  useEffect(() => {
    const wasInactive = prevActiveRef.current === false;
    prevActiveRef.current = active;

    if (!wasInactive || !active) {
      return;
    }

    if (productData.id && productData.id !== idProductHistory) {
      setLoading(true);
      setInputDeliveryError(false);
      setInputInviteAffiliateError(false);

      setLoading(false);
      setInputDelivery(idProductHistory === productData.id ? inputDelivery : '');
      setInputInviteAffiliate(idProductHistory === productData.id ? inputInviteAffiliate : '');
      setInputInviteAffiliateError(false);
      setStatusProduct(productData.status);
      setProductInvoiceData(null);
      setNumberAffiliates(productData.affiliates);
    } else {
      setLoading(false);
      setActionButtonDisabled(false);
    }
  }, [active, productData, idProductHistory, inputDelivery, inputInviteAffiliate]);

  if (!productData) {
    return (
      <Drawer
        onClose={handleCloseQuickView}
        placement='right'
        open={false}
        closable={false}
        width='min(450px, 100vw)'
      />
    );
  }

  const productDataReceive = productData;

  let labelToggleStatus: React.ReactNode = null;

  if (statusMutation.isPending) {
    labelToggleStatus = <Spin size='small' />;
  } else if (statusProduct === 'active') {
    labelToggleStatus = 'Ativo';
  } else if (statusProduct === 'inactive') {
    labelToggleStatus = 'Inativo';
  } else if (statusProduct === 'filed') {
    labelToggleStatus = 'Arquivado';
  } else {
    labelToggleStatus = 'Sem status';
  }

  return (
    <>
      <Drawer
        onClose={handleCloseQuickView}
        placement='right'
        open={active}
        width='min(450px, 100vw)'
        title={`Produto: ${productDataReceive.id}`}
      >
        {loading ? (
          <div className='flex h-full items-center justify-center'>
            <Spin tip='Carregando produto ...' />
          </div>
        ) : (
          <div className='flex flex-col gap-4'>
            <div className='flex items-start gap-3'>
              {productDataReceive.image ? (
                <img src={productDataReceive.image} alt='' className='size-20 shrink-0 rounded-lg object-cover' />
              ) : (
                <div className='size-20 shrink-0 rounded-lg bg-(--uizz-antd-color-fill-tertiary)' />
              )}

              <div className='flex min-w-0 flex-1 flex-col gap-2'>
                <Typography.Text strong ellipsis title={productDataReceive.title || 'Produto desconhecido'}>
                  {productDataReceive.title || 'Produto desconhecido'}
                </Typography.Text>

                <div className='flex items-center gap-2'>
                  <Switch
                    size='small'
                    defaultChecked={statusProduct === 'active'}
                    checked={statusProduct === 'active'}
                    disabled={true}
                    onChange={handleChangeStatusProduct}
                  />
                  <Typography.Text type='secondary'>{labelToggleStatus}</Typography.Text>
                </div>

                {!productDataReceive.has_children && (
                  <div className='flex flex-col items-baseline'>
                    <Typography.Text type='secondary' className='text-xs tracking-wide uppercase'>
                      Preço
                    </Typography.Text>
                    <PermissionHide inverse action='myeduzz:meus-produtos:ocultar-valores'>
                      <Typography.Text strong type='success' className='text-lg leading-none'>
                        {productDataReceive.charge_type === 'A' && (
                          <FormattedNumber value={productDataReceive.recurring_value} format='BRL' />
                        )}
                        {productDataReceive.charge_type === 'N' && (
                          <FormattedNumber value={productDataReceive.price} format='BRL' />
                        )}
                        {productDataReceive.charge_type === 'G' && 'Grátis'}
                        {(!productDataReceive.charge_type ||
                          productDataReceive.charge_type === '' ||
                          productDataReceive.charge_type === ' ') && <FormattedNumber value={0} format='BRL' />}
                      </Typography.Text>
                    </PermissionHide>
                    <PermissionHide inverse={false} action='myeduzz:meus-produtos:ocultar-valores'>
                      <Typography.Text type='secondary'>oculto</Typography.Text>
                    </PermissionHide>
                    {productDataReceive.charge_type === 'A' && (
                      <Typography.Text type='secondary' className='text-xs'>
                        recorrente
                      </Typography.Text>
                    )}
                  </div>
                )}
              </div>
            </div>

            {!productDataReceive.has_children && (
              <Card size='small'>
                <div className='grid grid-cols-3 divide-x divide-(--uizz-antd-color-border)'>
                  <div className='flex flex-col items-center gap-0.5 px-2'>
                    <Typography.Text type='secondary'>Ganhos</Typography.Text>
                    <Typography.Text strong type='success'>
                      <PermissionHide inverse action='myeduzz:meus-produtos:ocultar-valores'>
                        <ColoredFormattedNumber
                          value={productDataReceive.total_gains}
                          format={productDataReceive.currency.code}
                        />
                      </PermissionHide>
                      <PermissionHide inverse={false} action='myeduzz:meus-produtos:ocultar-valores'>
                        oculto
                      </PermissionHide>
                    </Typography.Text>
                  </div>

                  <div className='flex flex-col items-center gap-0.5 px-2'>
                    <Typography.Text type='secondary'>Afiliados</Typography.Text>
                    <Typography.Text strong>{numberAffiliates}</Typography.Text>
                  </div>

                  <div className='flex flex-col items-center gap-0.5 px-2'>
                    <Typography.Text type='secondary'>Funil Infinito</Typography.Text>
                    <Typography.Text strong type={productDataReceive.infinity_funnel ? 'success' : undefined}>
                      {productDataReceive.infinity_funnel ? 'Ativado' : 'Desativado'}
                    </Typography.Text>
                  </div>
                </div>

                <div className='mt-3 grid grid-cols-3 divide-x divide-(--uizz-antd-color-border) border-t border-(--uizz-antd-color-border) pt-3'>
                  <div className='flex flex-col items-center gap-0.5 px-2'>
                    <Typography.Text type='secondary'>Comissão</Typography.Text>
                    <Typography.Text strong>
                      <PermissionHide inverse action='myeduzz:meus-produtos:ocultar-valores'>
                        {productDataReceive.charge_type === 'A' && (
                          <CalculateCommissionAffiliate
                            price={productDataReceive.recurring_value}
                            commissionType={productDataReceive.recurring_comission_type}
                            commissionValue={productDataReceive.recurring_comission_value}
                          />
                        )}
                        {productDataReceive.charge_type === 'N' && (
                          <CalculateCommissionAffiliate
                            price={productDataReceive.price || 0}
                            commissionType={productDataReceive.comission_type}
                            commissionValue={productDataReceive.comission_value}
                          />
                        )}
                        {(!productDataReceive.charge_type ||
                          productDataReceive.charge_type === ' ' ||
                          productDataReceive.charge_type === '') && (
                          <CalculateCommissionAffiliate price={0} commissionValue={0} />
                        )}
                      </PermissionHide>
                      <PermissionHide inverse={false} action='myeduzz:meus-produtos:ocultar-valores'>
                        oculto
                      </PermissionHide>
                    </Typography.Text>
                  </div>

                  <div className='flex flex-col items-center gap-0.5 px-2'>
                    <Typography.Text type='secondary'>RPC/A</Typography.Text>
                    <Typography.Text strong>{productDataReceive.rpc_affiliate || 0}</Typography.Text>
                  </div>

                  <div className='flex flex-col items-center gap-0.5 px-2'>
                    <Typography.Text type='secondary'>RPC/P</Typography.Text>
                    <Typography.Text strong>{productDataReceive.rpc_producer || 0}</Typography.Text>
                  </div>
                </div>
              </Card>
            )}

            <div className='flex flex-col gap-2'>
              <Typography.Text type='secondary'>Ações Rápidas</Typography.Text>

              {((!productDataReceive.has_children && statusProduct && statusProduct === 'active') ||
                statusProduct === 'inactive') && <QuickViewLinks product={productDataReceive} />}

              {statusProduct === 'active' && (
                <ActionCollapse title='Adicionar Afiliados' icon={<Icon.Users size={16} />}>
                  <CustomCopyToClipboard
                    text={`${HOST_ORBITA}/affiliate/invite/${productDataReceive.id}`}
                    disableUnderline={true}
                    fullWidth
                  />
                  <div className='mt-4'>
                    <Form.Item label='Convide por E-mail' className='mb-0'>
                      <Input
                        id='invite-email'
                        type='text'
                        size='small'
                        placeholder='E-mail do afiliado'
                        onChange={handleChange('inputInviteAffiliate')}
                        value={inputInviteAffiliate}
                        autoFocus={true}
                        status={isValid('inputInviteAffiliate') ? 'error' : undefined}
                        suffix={
                          <Tooltip title='Enviar'>
                            <span
                              onClick={handleClickInviteAffiliate}
                              className='ml-2 inline-flex cursor-pointer items-center border-l border-(--uizz-antd-color-border-secondary) pl-2'
                            >
                              {inviteAffiliateMutation.isPending ? <Spin size='small' /> : <Icon.Send size={16} />}
                            </span>
                          </Tooltip>
                        }
                      />
                    </Form.Item>
                    {isValid('inputInviteAffiliate') && (
                      <Typography.Text type='danger'>{errorFor('inputInviteAffiliate')}</Typography.Text>
                    )}
                  </div>
                </ActionCollapse>
              )}
            </div>
          </div>
        )}
      </Drawer>

      <ModalActions
        open={modalStatusProduct}
        onClose={onCancelModal}
        onOk={onConfirmModal}
        title={`Deseja realmente ${statusProduct === 'active' ? 'inativar' : 'ativar'} este produto?`}
        buttonOk={statusProduct === 'active' ? 'Inativar' : 'Ativar'}
      />

      <ModalActions
        open={modalStatusProductArchive}
        onClose={onCancelModalArchive}
        onOk={onConfirmModalArchive}
        title={
          'Deseja realmente excluir este produto? Atenção, esta operação é irreversível e automaticamente todas variações serão arquivadas!'
        }
        buttonOk={'Excluir'}
      />

      <ModalActions
        open={modalCloneProduct}
        onClose={onCancelModalCloneProduct}
        onOk={onConfirmModalCloneProduct}
        title={'Deseja realmente duplicar este produto? Obs: você será redirecionado para a tela de edição.'}
        buttonOk={'Duplicar'}
      />
    </>
  );
};

export default QuickView;
